import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ActionIcon, Group, Select, Tooltip, type ComboboxItem } from "@mantine/core";
import { IconBuildings, IconPlus } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type { AdminCompanyListItem, ApiResponse } from "@/types";
import { useAuthStore } from "@/stores/auth";
import { useUiStore } from "@/stores/ui";
import { useInboxStore } from "@/stores/inbox";
import { usePresenceStore } from "@/stores/presence";
import { CreateCompanyModal } from "@/components/layout/CreateCompanyModal";

/**
 * Resets company-scoped client state and navigates to a safe route. Shared by
 * the switcher and the admin "open in dashboard" action so switching always
 * behaves the same: inbox filters/presence don't leak across companies and a
 * stale /inbox/:conversationId route is dropped.
 */
export function useSwitchCompany() {
  const navigate = useNavigate();
  const setCurrentCompanyId = useUiStore((s) => s.setCurrentCompanyId);
  const setAdminJumpCompanyId = useUiStore((s) => s.setAdminJumpCompanyId);

  return (companyId: string, opts?: { adminJump?: boolean }) => {
    if (opts?.adminJump) {
      setAdminJumpCompanyId(companyId);
    } else {
      setCurrentCompanyId(companyId);
    }
    useInboxStore.getState().reset();
    usePresenceStore.getState().reset();
    navigate("/inbox");
  };
}

export function CompanySwitcher() {
  const companies = useAuthStore((s) => s.companies);
  const isSystemAdmin = useAuthStore((s) => s.isSystemAdmin);
  const currentCompanyId = useUiStore((s) => s.currentCompanyId);
  const adminJumpCompanyId = useUiStore((s) => s.adminJumpCompanyId);
  const switchCompany = useSwitchCompany();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const options: ComboboxItem[] = companies.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  // A system admin visiting a non-member company: synthesize an option so the
  // Select shows where they are.
  const jumped =
    adminJumpCompanyId && !companies.some((c) => c.id === adminJumpCompanyId);
  const jumpedCompany = useQuery({
    queryKey: queryKeys.admin.company(adminJumpCompanyId ?? ""),
    queryFn: () =>
      api.get<ApiResponse<AdminCompanyListItem>>(
        `/admin/companies/${adminJumpCompanyId}`,
      ),
    enabled: !!jumped && isSystemAdmin,
  });
  if (jumped) {
    options.push({
      value: adminJumpCompanyId,
      label: jumpedCompany.data
        ? `${jumpedCompany.data.data.name} (admin)`
        : "Loading…",
    });
  }

  // Default to first company if none selected; validate stale persisted ids.
  const value = jumped
    ? adminJumpCompanyId
    : currentCompanyId && companies.some((c) => c.id === currentCompanyId)
      ? currentCompanyId
      : (companies[0]?.id ?? null);

  return (
    <Group gap={4} wrap="nowrap">
      <Select
        data={options}
        value={value}
        onChange={(val) => {
          if (val && val !== value) switchCompany(val);
        }}
        placeholder="Select company"
        size="sm"
        variant="filled"
        allowDeselect={false}
        styles={{
          root: { minWidth: 160 },
        }}
      />
      <Tooltip label="Create company">
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label="Create company"
          onClick={() => setCreateOpen(true)}
        >
          <IconPlus size={16} />
        </ActionIcon>
      </Tooltip>
      {isSystemAdmin && (
        <Tooltip label="Browse all companies">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Browse all companies"
            onClick={() => navigate("/admin/companies")}
          >
            <IconBuildings size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      <CreateCompanyModal opened={createOpen} onClose={() => setCreateOpen(false)} />
    </Group>
  );
}
